/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { switchMap, map, mergeMap } from 'rxjs/operators';
import { Observable, type ObservableInput, of } from 'rxjs';

import { dispatch } from '../../store/state/state-manager';
import { postProcess, removeContainerItem } from '../../store/state/actions/action-creators';
import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getActionPayload, processDataToDispatchErrorAction } from '../../store/state/epics/util';
import { mergeInstructions } from '../../page-instructions';
import { SUBMIT_EMBEDDED_DATA_MODAL, ERROR } from '../../store/state/actions';
import { hasErrorActionType } from '../common-helper';
import { executeRequest } from '../open-embedded-data-modal/async-request';
import { refreshAssignment, dispatchCaseViewRefresh } from '../../refresh/refreshHelpers';
import ContextProcessor from '../../context/context-processor';
import type { ActionPayload, SubmitEmbedDataAction, ReturnedResponseType } from '../open-embedded-data-modal/types';
import ActionsSequencer from '../../actions/sequencer';
import objectTransformer from '../../utils/object-transformer';
import type { DxAPIResponseObject } from '../types';
import * as actions from '../../store/state/actions/action-creators';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import stateMachineManager from '../../page-instructions/state-machine-manager';

const mergeDataAfterSubmit = (chainedData: ReturnedResponseType, actionInPayload: ActionPayload) => {
  if (chainedData && actionInPayload.editType === 'action' && chainedData.type !== ERROR) {
    const { context, pageReference } = actionInPayload;
    const commonInfo = { context };
    const { response } = chainedData as { response: DxAPIResponseObject };

    const isObjectPageReference = pageReference?.startsWith(publicConstants.OBJECT_INFO.OBJECT_INFO);
    let responseJSON = null;

    if (isObjectPageReference) {
      responseJSON = objectTransformer.transformCaseData(response);
      responseJSON.uiResources.root.config.context = pageReference;
    } else {
      responseJSON = response;
    }
    const translatedDXAPIResponse = getTranslatedDXAPIResponse(responseJSON);
    translatedDXAPIResponse.key = actionInPayload.ID;

    if (translatedDXAPIResponse.data?.caseInfo) {
      translatedDXAPIResponse.data.caseInfo.ID = actionInPayload.ID;
    } else if (translatedDXAPIResponse.data?.objectInfo) {
      translatedDXAPIResponse.data.objectInfo.ID = actionInPayload.ID;
    }

    const mergePayload = getActionPayload(translatedDXAPIResponse, commonInfo);
    mergePayload.skipValidation = true;
    const mergeAction = actions.postProcess(actions.mergeData, mergePayload);

    stateMachineManager.registerForServerChangesSync();
    dispatch(mergeAction);
    stateMachineManager.unRegisterForServerChanges();
  }
  return chainedData;
};

export const submitEmbeddedDataModal = (action$: Observable<SubmitEmbedDataAction>) => {
  let actionMgrID: string | number = '';
  let actionsOut: SubmitEmbedDataAction[] = [];
  let actionInPayload: ActionPayload;

  return action$.pipe(
    ofType(SUBMIT_EMBEDDED_DATA_MODAL),
    switchMap((action: SubmitEmbedDataAction) => {
      actionInPayload = action.payload;
      const { isFormValid } = action.payload;
      ({ actionMgrID } = action.payload);

      if (!isFormValid) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        ActionsSequencer.handleDeferredActionCompletion(action.payload.context);
        // return empty observable as form is not valid.
        return of([]);
      }

      return of(action.payload).pipe(
        mergeMap<ActionPayload, ObservableInput<ReturnedResponseType>>((payload: ActionPayload) => {
          actionInPayload = payload;
          return executeRequest(actionInPayload);
        }),
        mergeMap(async (chainedData: ReturnedResponseType) => {
          return mergeDataAfterSubmit(chainedData, actionInPayload);
        }),
        mergeMap(async (chainedData: ReturnedResponseType) => {
          if (chainedData && chainedData.type === ERROR) {
            const errorAction = processDataToDispatchErrorAction(chainedData);
            actionsOut.push(errorAction);
            return actionsOut;
          }
          const { containerItemID, context, pageReference, interestPage } = actionInPayload;
          const targetPage = interestPage?.replace(/\(\d+\)$/, '');

          mergeInstructions(
            context,
            actionInPayload.parentContext,
            pageReference.startsWith(publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT)
              ? constants.getProperty(publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT)
              : constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_CONTENT),
            false,
            targetPage
          );

          actionsOut.push(
            postProcess(removeContainerItem, {
              containerItemID,
              context,
              target: actionInPayload.target,
              skipReleaseLockRequest: true
            })
          );
          return actionsOut;
        }),
        map(() => {
          if (hasErrorActionType(actionsOut)) {
            ActionManagerInstance.rejectActionByID(actionMgrID);
            ActionsSequencer.handleDeferredActionCompletion(actionInPayload.context);
          } else {
            ActionManagerInstance.resolveActionByID(actionMgrID);
            ActionsSequencer.handleDeferredActionCompletion(actionInPayload.context);
            const { pageReference } = action.payload;
            const isMultiRecordData = ContextProcessor.getPropertyValue(
              constants.CONTEXT_DATA_MODAL_VIEW.IS_MULTI_RECORD_DATA,
              pageReference,
              actionInPayload.parentContext
            );
            if (isMultiRecordData) {
              const caseID = ContextProcessor.getPropertyValue(
                constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID),
                pageReference,
                actionInPayload.parentContext
              );
              const viewID = ContextProcessor.getPropertyValue(
                constants.CONTEXT_DATA_MODAL_VIEW.VIEW_NAME,
                pageReference,
                actionInPayload.parentContext
              );
              const parentPageRef = actionInPayload.pageReference?.substring(
                0,
                actionInPayload.pageReference?.lastIndexOf('.')
              );
              const parentInterestPage = parentPageRef.replace(
                constants.getProperty(constants.CASE_INFO.CASE_INFO_CONTENT),
                ''
              );
              dispatchCaseViewRefresh(caseID, viewID, actionInPayload.parentContext, parentInterestPage, {});
            } else {
              refreshAssignment(
                actionInPayload.parentContext,
                constants.getProperty(constants.CASE_INFO.CASE_INFO_CONTENT),
                {}
              );
            }
          }
          dispatch(actionsOut);
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export default submitEmbeddedDataModal;
